import { useCompetencia } from '@/global/components/Selects/SelectCompetencia'
import { useAuth } from '@/hooks'
import { abonos, createAbonoRecorrenteSchema } from '@/service/api'
import { CategoriaEnum } from '@acme/auth'
import { Divider, Grid } from '@mui/material'
import dayjs from 'dayjs'
import {
    FormField,
    FormFieldAutocomplete,
    FormFieldSelect,
    FormFile,
} from 'global/components/Form'
import IconeAjuda from 'global/components/IconeAjuda'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { z } from 'zod'
import { buildAbono } from '../../Ausencias/modals/AdicionarAbono'
import CalendarioAbonos from '../atoms/CalendarioAbonos'
import RepeticaoAbonoAccordion from '../atoms/RepeticaoAbonoAccordion'
import TabFuncionariosForm from '../atoms/TabsFuncionarios'

export default function FormNovoAbono() {
    const {
        decoded: { categoria },
    } = useAuth()
    const { watch, setValue } =
        useFormContext<z.infer<typeof createAbonoRecorrenteSchema>>()
    const {
        data: dataAbono,
        tipoRepeticao,
        repeticaoMensalOpcao,
        every,
        dataFinal,
        funcionarioId,
        ...rest
    } = watch()
    const [mes, setMes] = useCompetencia()

    useEffect(() => {
        if (dataAbono) setMes(dayjs(dataAbono).format('YYYY-MM'))
    }, [dataAbono])

    const MemoizedFuncionariosForm = useMemo(
        () => (
            <TabFuncionariosForm
                funcionarioIds={funcionarioId}
                onChange={v => setValue('funcionarioId', v)}
            />
        ),
        [funcionarioId],
    )

    const datas = useMemo(
        () =>
            buildAbono(
                {
                    ...rest,
                    justificativa: rest.justificativa || undefined,
                    data: dataAbono,
                },
                {
                    tipoRepeticao,
                    repeticaoMensalOpcao,
                    every,
                    dataFinal,
                },
            ).data,
        [dataAbono, tipoRepeticao, repeticaoMensalOpcao, every, dataFinal],
    )

    return (
        <Grid container spacing={2}>
            {MemoizedFuncionariosForm}
            <Grid item xs={12}>
                <Divider>Detalhes do abono</Divider>
            </Grid>
            <Grid item xs={6} sm={4}>
                <FormField
                    name="data"
                    label="Data de abono"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    required
                />
            </Grid>
            <Grid item xs={6} sm={3}>
                <FormField
                    name="quantidadeHoras"
                    label="Horas abonadas"
                    required
                    InputLabelProps={{ shrink: true }}
                    placeholder="HH:mm"
                    options={{
                        blocks: [2, 2],
                        delimiters: [':'],
                        numericOnly: true,
                    }}
                />
            </Grid>
            <Grid item xs={12} sm={5}>
                <FormFieldSelect
                    label="Tipo de abono"
                    name="tipo"
                    options={[
                        { label: 'Normal', value: 'NORMAL' },
                        {
                            label: 'Saúde Ocupacional',
                            value: 'SAUDE_OCUPACIONAL',
                        },
                    ]}
                    endAdornment={
                        <IconeAjuda className="mr-4">
                            O abono de Saúde Ocupacional é concedido para
                            atestados médicos e afins.
                            <br />
                            <br />
                            Para o relatório de absenteísmo: um abono normal
                            contabiliza ausências justificadas, já o abono de
                            saúde ocupacional é considerado como horas
                            trabalhadas e não soma faltas no dia.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item xs={12}>
                <Divider>
                    Motivos do abono (preencha pelo menos um campo)
                </Divider>
            </Grid>
            <Grid item xs={12}>
                <AutocompleteDescricaoAbono />
            </Grid>
            <Grid item xs={12}>
                <FormField
                    label="Justificativa"
                    name="justificativa"
                    multiline
                    rows={3}
                    max={190}
                    InputProps={{
                        endAdornment: (
                            <IconeAjuda>
                                Justificativa do abono, é uma explicação sobre o
                                motivo do abono. (Máximo de 190 caracteres)
                                <br />
                                <br />A justificativa é opcional, mas é
                                recomendada para fornecer contexto adicional
                                sobre o abono. Ela não é exibida no espelho do
                                ponto e só pode ser visualizada ao editar ou ver
                                detalhes do abono.
                            </IconeAjuda>
                        ),
                    }}
                />
            </Grid>
            <Grid item xs={12}>
                <FormFile label="Anexo" name="fileUrl" />
            </Grid>
            {categoria !== CategoriaEnum.Funcionario && [
                <Grid item xs={12} key="1">
                    <Divider>Recorrência (opcional)</Divider>
                </Grid>,
                <Grid item xs={6} key="2">
                    <FormField
                        name="dataFinal"
                        label="Fim da repetição"
                        type="date"
                        autoComplete="off"
                    />
                </Grid>,
                <Grid item xs={12} key="3">
                    <RepeticaoAbonoAccordion />
                </Grid>,
                <Grid item xs={12} key="4">
                    <Divider>
                        Prévia do lançamento (apenas visualização)
                    </Divider>
                </Grid>,
                <Grid item xs={12} key="5">
                    <CalendarioAbonos
                        key={mes}
                        value={mes}
                        onChange={setMes}
                        datas={datas}
                    />
                </Grid>,
            ]}
        </Grid>
    )
}

export function AutocompleteDescricaoAbono() {
    const { data = [] } = abonos.useFetchById<string[]>('descricoes', {
        enabled: true,
        refetchOnWindowFocus: false,
    })
    const [digitando, setDigitando] = useState<string>('')

    const handleInputChange = (inputValue: string) => {
        setDigitando(inputValue.trim())
    }

    const criarNovaDescricao = () => {
        if (digitando && !data.includes(digitando)) {
            abonos.setQueryData<string[]>(
                { id: 'descricoes' },
                (prev: string[] | undefined) =>
                    prev ? [digitando, ...prev] : [digitando],
            )
        }
        setDigitando('')
    }

    return (
        <FormFieldAutocomplete
            name="descricao"
            label="Descrição do abono"
            options={data}
            onInputChange={handleInputChange}
            creatableOption={
                digitando.length > 0 && !data.includes(digitando)
                    ? {
                          label: `Criar nova descrição: "${digitando}"`,
                          value: digitando,
                          onClick: criarNovaDescricao,
                      }
                    : undefined
            }
            noOptionsText="Nenhuma descrição encontrada, comece a digitar para criar uma nova descrição."
            InputProps={{
                endAdornment: (
                    <IconeAjuda className="-mt-5">
                        Descrição do abono, pode ser uma justificativa curta ou
                        motivo específico. (Máximo de 30 caracteres)
                        <br />
                        <br />
                        Se a descrição não existir, você pode começar a digitar
                        e criar uma nova clicando no botão "Criar nova
                        descrição".
                        <br />
                        <br />
                        As descrições são úteis para categorizar e identificar
                        rapidamente o motivo do abono, elas são exibidas no
                        espelho do ponto.
                    </IconeAjuda>
                ),
            }}
        />
    )
}
